import { z } from 'zod';
import { ContentSummarySchema } from '../content.schema';
import { WriterRoomStatusSchema } from '../room-status.schema';
import { DateSchema } from './base.schema';
import { FieldChangeSchema } from './event-change.schema';
import { SectionDefinitionSchema } from './settings-section.schema';
import { GraphPositionSchema } from './shared.schema';

/**
 * Record of a change TO an entity BY an event, stored in summaries.
 */
export const SummaryEventChangeSchema = z.object({
  eventId: z.string(),
  description: z.string().optional(),
  changes: z.array(FieldChangeSchema),
});

export type SummaryEventChange = z.infer<typeof SummaryEventChangeSchema>;

/**
 * Base Entity Summary schema for quick reference and listing.
 */
export const EntitySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  fullName: z.string().optional(),
  nickname: z.string().optional(),
  type: z.string(),
  shortDescription: z.string(),
  mainImageUrl: z.string().optional(),
  mainImagePath: z.string().optional(),
  primaryImageId: z.string().uuid().nullable(),
  mapImageUrl: z.string().optional().nullable(),
  mapImageStoragePath: z.string().optional().nullable(),
  graphPosition: GraphPositionSchema.optional(),
  graphPositionUpdatedBy: z.string().optional(),
  graphPositionUpdatedAt: DateSchema.optional(),
  parentId: z.string().optional().describe('Parent ID for hierarchical entities.'),
  order: z.number().int().optional(),
  updatedAt: DateSchema,
  eventChanges: z.record(z.string(), SummaryEventChangeSchema).optional(),
});

export type EntitySummary = z.infer<typeof EntitySummarySchema>;

/**
 * Event-specific Summary schema.
 * TODO: reduce, it's too large, who is using all that info from summary?
 */
export const EventSummarySchema = EntitySummarySchema.extend({
  date: z.string().optional().describe('Used for timeline events.'),
  locationId: z.string().optional().describe('Primary location ID for events.'),
  characterIds: z.array(z.string()).optional().describe('Involved characters.'),
  organizationIds: z.array(z.string()).optional().describe('Involved organizations.'),
  locationIds: z.array(z.string()).optional().describe('All involved locations.'),
  arcId: z.string().optional().describe('ID of the arc this event belongs to.'),
  structureSectionId: z.string().optional().describe('Reference to the structure section this event belongs to'),
  isNarrativelyComplete: z.boolean().optional().describe('Whether the event is narratively complete.'),
  causesEventIds: z.array(z.string()).optional().describe('Event IDs that this event causes.'),
});

export type EventSummary = z.infer<typeof EventSummarySchema>;

/**
 * Narrative Summary schema (for scenes, chapters, arcs, etc.)
 */
export const NarrativeSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().optional(),
  type: z.string().optional(),
  shortDescription: z.string().optional(),
  preview: z.string().optional(),
  updatedAt: DateSchema,

  // Listing/Mapping fields
  chapterId: z.string().optional().nullable().describe('ID of the chapter this scene belongs to.'),
  eventIds: z.array(z.string()).optional().describe('Events taking place in this node.'),
  characterIds: z.array(z.string()).optional().describe('Characters appearing in this node.'),
  locationIds: z.array(z.string()).optional().describe('Locations appearing in this node.'),
  organizationIds: z.array(z.string()).optional().describe('Organizations appearing in this node.'),
  povCharacterId: z.string().optional().describe('POV Character ID.'),
  status: WriterRoomStatusSchema.optional().describe('Current status.'),
  wordCount: z.number().optional().describe('Word count.'),
  goal: z.string().optional().describe('Goal.'),
  conflict: z.string().optional().describe('Conflict.'),
  isNarrativelyComplete: z.boolean().optional(),
  isArchive: z.boolean().optional().describe('Whether this item is archived.'),
  mainContentId: z.string().optional().describe('ID of the Content object associated with this node.'),
  contentVariants: z.array(z.string()).optional().describe('Alternative content documents associated with this node.'),
  contentVariantVersions: z.record(z.string(), z.number().int().nonnegative()).optional().describe('Latest scratchpad version for every scene content variant.'),
  chapterPublicId: z.string().optional().describe('Stable public chapter identifier.'),
  publishedContentId: z.string().optional().describe('Content identifier most recently published.'),
  publishStatus: z.enum(['draft', 'preview', 'public']).optional().describe('Publication status of this narrative item.'),
  compiledSceneVersions: z.record(z.string(), z.string()).optional().describe('Mapping of scene ID to compiled updatedAt timestamp.'),
  variantSceneRefs: z.record(z.string(), z.record(z.string(), z.object({
    contentId: z.string(),
    version: z.number().int().nonnegative(),
  }))).optional().describe('Chapter content variant ID to scene variant/version associations.'),
});

export type NarrativeSummary = z.infer<typeof NarrativeSummarySchema>;

/**
 * Metadata structures for SPLIT documents (one document per type).
 */
export const EntityTypeMetaSchema = z.object({
  summaries: z.record(z.string(), z.union([EventSummarySchema, EntitySummarySchema])),
  settings: z.array(SectionDefinitionSchema),
});
export type EntityTypeMeta = z.infer<typeof EntityTypeMetaSchema>;

export const NarrativeTypeMetaSchema = z.object({
  summaries: z.record(z.string(), NarrativeSummarySchema),
});
export type NarrativeTypeMeta = z.infer<typeof NarrativeTypeMetaSchema>;

/**
 * Storage modes for layout.
 */
export const StorageModeSchema = z.enum(['embedded', 'split']);
export type StorageMode = z.infer<typeof StorageModeSchema>;

export const TypeStorageLayoutSchema = z.object({
  items: StorageModeSchema,
  meta: StorageModeSchema,
});
export type TypeStorageLayout = z.infer<typeof TypeStorageLayoutSchema>;

export const NovelStorageLayoutSchema = z.object({
  entities: z.record(z.string(), TypeStorageLayoutSchema),
  narrative: z.record(z.string(), TypeStorageLayoutSchema),
});
export type NovelStorageLayout = z.infer<typeof NovelStorageLayoutSchema>;

/**
 * Metadata structures for EMBEDDED documents (inside novel doc).
 */
export const EntitiesMetaSchema = z.object({
  summaries: z.record(z.string(), z.record(z.string(), z.union([EventSummarySchema, EntitySummarySchema]))).default({}),
  settings: z.record(z.string(), z.array(SectionDefinitionSchema)).default({}),
  codexCategories: z.record(z.string(), z.string()).optional().default({}), // ID -> Name mapping for dynamic codex categories
  codexCategoryParents: z.record(z.string(), z.string()).optional().default({}),
});
export type EntitiesMeta = z.infer<typeof EntitiesMetaSchema>;

export const NarrativeMetaSchema = z.object({
  summaries: z.object({
    contents: z.record(z.string(), ContentSummarySchema).optional().default({}),
  }).catchall(z.record(z.string(), NarrativeSummarySchema)).default({}),
});
export type NarrativeMeta = z.infer<typeof NarrativeMetaSchema>;
